import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { View, Text, StyleSheet, Platform, Animated, Dimensions, TouchableOpacity, Image, Easing } from 'react-native';
import { BlurView } from 'expo-blur';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '../context/NavigationContext';
import { useTicker } from '../context/TickerContext';
import { useBackgroundQuery } from '../context/BackgroundQueryContext';
import { useAuth } from '../context/AuthContext';
import { getTeamLogo } from '../data/logos';

const { width: screenWidth } = Dimensions.get('window');

// Skeleton shimmer animation component
const SkeletonShimmer = ({ width, height, borderRadius = 4, style }) => {
  const shimmerAnim = useRef(new Animated.Value(0)).current;
  
  useEffect(() => {
    const shimmer = Animated.loop(
      Animated.timing(shimmerAnim, {
        toValue: 1,
        duration: 1500,
        easing: Easing.linear,
        useNativeDriver: false,
      })
    );
    shimmer.start();
    return () => shimmer.stop();
  }, []);
  
  const translateX = shimmerAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [-width, width],
  });
  
  return (
    <View style={[{ 
      width, 
      height, 
      borderRadius,
      backgroundColor: 'rgba(255,255,255,0.08)',
      overflow: 'hidden',
    }, style]}>
      <Animated.View style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        transform: [{ translateX }],
      }}>
        <View style={{
          width: width * 0.5,
          height: '100%',
          backgroundColor: 'rgba(255,255,255,0.05)',
          ...(Platform.OS === 'web' ? {
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
          } : {}),
        }} />
      </Animated.View>
    </View>
  );
};

// Single skeleton ticker item
const SkeletonTickerItem = ({ index }) => {
  return (
    <View style={styles.tickerItem}>
      {/* Status badge skeleton */}
      <SkeletonShimmer width={50} height={18} borderRadius={4} />
      
      {/* League skeleton */}
      <SkeletonShimmer width={30} height={12} borderRadius={3} />
      
      {/* Teams skeleton */}
      <View style={styles.teamsContainer}>
        <SkeletonShimmer width={18} height={18} borderRadius={9} />
        <SkeletonShimmer width={60} height={14} borderRadius={3} />
        <View style={{ width: 12 }} />
        <SkeletonShimmer width={60} height={14} borderRadius={3} />
        <SkeletonShimmer width={18} height={18} borderRadius={9} />
      </View>
      
      {/* Time skeleton */}
      <SkeletonShimmer width={70} height={12} borderRadius={3} />
      
      {/* Divider */}
      <View style={styles.divider} />
    </View>
  );
};

// Team logo placeholder - shows team initial
const TeamLogo = ({ logoUrl, teamName, size = 20 }) => {
  const [hasError, setHasError] = useState(false);
  const initial = teamName ? teamName.charAt(0).toUpperCase() : '?';
  
  if (logoUrl && !hasError) {
    return (
      <Image 
        source={{ uri: logoUrl }} 
        style={{ width: size, height: size, borderRadius: size / 2 }}
        resizeMode="contain"
        onError={() => setHasError(true)}
      />
    );
  }
  
  // Fallback to initial
  return (
    <View style={{
      width: size,
      height: size,
      borderRadius: size / 2,
      backgroundColor: 'rgba(255,255,255,0.15)',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <Text style={{
        fontSize: size * 0.5,
        fontWeight: '700',
        color: 'rgba(255,255,255,0.7)',
        fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
      }}>
        {initial}
      </Text>
    </View>
  );
};

// Helper to format team/player names based on user preference
const formatName = (fullName, format, isTeam = true) => {
  if (!fullName || fullName === 'TBD') return fullName;
  
  const name = fullName.trim();
  
  switch (format) {
    case 'full':
      // Return full name as-is
      return name;
      
    case 'abbreviated':
      if (isTeam) {
        // For teams: Try to get standard abbreviation or create one
        // Common team abbreviations
        const teamAbbreviations = {
          'cleveland cavaliers': 'CLE', 'golden state warriors': 'GSW', 'los angeles lakers': 'LAL',
          'los angeles clippers': 'LAC', 'boston celtics': 'BOS', 'miami heat': 'MIA',
          'milwaukee bucks': 'MIL', 'phoenix suns': 'PHX', 'denver nuggets': 'DEN',
          'minnesota timberwolves': 'MIN', 'dallas mavericks': 'DAL', 'oklahoma city thunder': 'OKC',
          'new york knicks': 'NYK', 'brooklyn nets': 'BKN', 'philadelphia 76ers': 'PHI',
          'chicago bulls': 'CHI', 'toronto raptors': 'TOR', 'indiana pacers': 'IND',
          'detroit pistons': 'DET', 'atlanta hawks': 'ATL', 'charlotte hornets': 'CHA',
          'washington wizards': 'WAS', 'orlando magic': 'ORL', 'memphis grizzlies': 'MEM',
          'new orleans pelicans': 'NOP', 'houston rockets': 'HOU', 'san antonio spurs': 'SAS',
          'utah jazz': 'UTA', 'sacramento kings': 'SAC', 'portland trail blazers': 'POR',
          // NFL
          'kansas city chiefs': 'KC', 'buffalo bills': 'BUF', 'san francisco 49ers': 'SF',
          'dallas cowboys': 'DAL', 'philadelphia eagles': 'PHI', 'green bay packers': 'GB',
          'new england patriots': 'NE', 'baltimore ravens': 'BAL', 'cincinnati bengals': 'CIN',
          'pittsburgh steelers': 'PIT', 'los angeles rams': 'LAR', 'los angeles chargers': 'LAC',
          'seattle seahawks': 'SEA', 'new york giants': 'NYG', 'new york jets': 'NYJ',
          'denver broncos': 'DEN', 'las vegas raiders': 'LV', 'arizona cardinals': 'ARI',
          'detroit lions': 'DET', 'chicago bears': 'CHI', 'minnesota vikings': 'MIN',
          'tampa bay buccaneers': 'TB', 'miami dolphins': 'MIA', 'cleveland browns': 'CLE',
          // MLB
          'new york yankees': 'NYY', 'new york mets': 'NYM', 'los angeles dodgers': 'LAD',
          'boston red sox': 'BOS', 'chicago cubs': 'CHC', 'chicago white sox': 'CWS',
          // NHL
          'vegas golden knights': 'VGK', 'colorado avalanche': 'COL', 'tampa bay lightning': 'TBL',
        };
        const lowerName = name.toLowerCase();
        if (teamAbbreviations[lowerName]) return teamAbbreviations[lowerName];
        // Fallback: take first 3 letters of last word
        const words = name.split(' ');
        return words[words.length - 1].substring(0, 3).toUpperCase();
      } else {
        // For players: "Carlos Alcaraz" -> "C. Alcaraz"
        const parts = name.split(' ');
        if (parts.length >= 2) {
          return `${parts[0].charAt(0)}. ${parts.slice(1).join(' ')}`;
        }
        return name;
      }
      
    case 'short':
    default:
      if (isTeam) {
        // For teams: Remove city name, keep team name
        // "Cleveland Cavaliers" -> "Cavaliers", "Los Angeles Lakers" -> "Lakers"
        const words = name.split(' ');
        if (words.length >= 2) {
          // Common two-word team names to keep together
          const twoWordTeams = ['trail blazers', 'red sox', 'white sox', 'blue jays', 'golden knights', '76ers'];
          const lastTwo = words.slice(-2).join(' ').toLowerCase();
          if (twoWordTeams.includes(lastTwo)) {
            return words.slice(-2).join(' ');
          }
          return words[words.length - 1];
        }
        return name;
      } else {
        // For players: Just last name "Carlos Alcaraz" -> "Alcaraz"
        const parts = name.split(' ');
        if (parts.length >= 2) {
          return parts[parts.length - 1];
        }
        return name;
      }
  }
};

// Helper to determine if name is likely a person (tennis, boxing, mma) vs team
const isPlayerSport = (league) => {
  const l = (league || '').toLowerCase();
  return l.includes('wta') || l.includes('atp') || l.includes('tennis') || 
         l.includes('ufc') || l.includes('mma') || l.includes('bellator') || l.includes('pfl') ||
         l.includes('boxing') || l.includes('golf') || l.includes('pga');
};

// Helper to parse various date formats
const parseDate = (startDate) => {
  if (!startDate) return null;
  
  try {
    // Handle Firestore timestamp format { _seconds, _nanoseconds }
    if (startDate._seconds) {
      return new Date(startDate._seconds * 1000);
    }
    // Handle Firestore timestamp format { seconds, nanoseconds }
    if (startDate.seconds) {
      return new Date(startDate.seconds * 1000);
    }
    // Handle ISO string or Date object
    return new Date(startDate);
  } catch (e) {
    console.warn('[GameTicker] Failed to parse date:', startDate);
    return null;
  }
};

// Helper function to determine game status
const getGameStatus = (startDate) => {
  const gameTime = parseDate(startDate);
  if (!gameTime) return { status: 'upcoming', label: 'UPCOMING', color: '#3B82F6', icon: 'time-outline' };
  
  try {
    const now = new Date();
    const diffMs = gameTime - now;
    const diffHours = diffMs / (1000 * 60 * 60);
    
    // Game started more than 3 hours ago = ended
    if (diffHours < -3) {
      return { status: 'ended', label: 'FINAL', color: '#6B7280', icon: 'checkmark-circle-outline' };
    }
    // Game is live or recently started (within last 3 hours)
    else if (diffHours < 0) {
      return { status: 'live', label: 'LIVE', color: '#10B981', icon: 'radio-button-on', pulse: true };
    }
    // Game starts within next hour = starting soon
    else if (diffHours < 1) {
      return { status: 'soon', label: 'SOON', color: '#F59E0B', icon: 'alarm-outline' };
    }
    // Upcoming
    else {
      return { status: 'upcoming', label: 'UPCOMING', color: '#3B82F6', icon: 'time-outline' };
    }
  } catch (e) {
    return { status: 'upcoming', label: 'UPCOMING', color: '#3B82F6', icon: 'time-outline' };
  }
};

// Format game time
const formatGameTime = (startDate) => {
  const gameTime = parseDate(startDate);
  if (!gameTime) return '';
  
  try {
    const now = new Date();
    const isToday = gameTime.toDateString() === now.toDateString();
    const isTomorrow = gameTime.toDateString() === new Date(now.getTime() + 86400000).toDateString();
    
    const timeStr = gameTime.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    
    if (isToday) return `Today ${timeStr}`;
    if (isTomorrow) return `Tomorrow ${timeStr}`;
    
    return gameTime.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (e) {
    return '';
  }
};

// Single game item in the ticker
const TickerItem = ({ fixture, onPress, onSGPPress, index, nameFormat, hasCachedSGPs = false, showSGPIndicator = false }) => {
  const status = getGameStatus(fixture.start_date);
  const pulseAnim = useRef(new Animated.Value(1)).current;
  
  // Pulse animation for live games
  useEffect(() => {
    if (status.pulse) {
      const pulse = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 0.5,
            duration: 800,
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 800,
            useNativeDriver: true,
          }),
        ])
      );
      pulse.start();
      return () => pulse.stop();
    }
  }, [status.pulse]);

  const homeTeamFull = fixture.home_team || 'TBD';
  const awayTeamFull = fixture.away_team || 'TBD';
  const league = (fixture.league || '').toLowerCase();
  
  // Better sport detection from league
  const detectSport = (leagueStr) => {
    const l = leagueStr.toLowerCase();
    if (l.includes('nba') || l.includes('ncaab') || l.includes('wnba')) return 'basketball';
    if (l.includes('nfl') || l.includes('ncaaf') || l.includes('cfl')) return 'football';
    if (l.includes('mlb')) return 'baseball';
    if (l.includes('nhl')) return 'hockey';
    if (l.includes('mls') || l.includes('epl') || l.includes('premier') || l.includes('la liga') || l.includes('bundesliga') || l.includes('serie a') || l.includes('champions') || l.includes('uefa')) return 'soccer';
    return fixture.sport || 'basketball';
  };
  
  const sport = detectSport(league);
  const isPlayer = isPlayerSport(league);
  
  // Format names based on user preference
  const homeTeam = formatName(homeTeamFull, nameFormat, !isPlayer);
  const awayTeam = formatName(awayTeamFull, nameFormat, !isPlayer);
  
  // Get logos - try fixture data first, then fall back to getTeamLogo lookup
  const homeLogo = fixture.home_logo || fixture.home_team_logo || fixture.home_image || getTeamLogo(homeTeamFull, sport);
  const awayLogo = fixture.away_logo || fixture.away_team_logo || fixture.away_image || getTeamLogo(awayTeamFull, sport);
  
  return (
    <TouchableOpacity 
      onPress={onPress}
      activeOpacity={0.7}
      style={styles.tickerItem}
    >
      {/* Status Badge */}
      <View style={[styles.statusBadge, { backgroundColor: `${status.color}20` }]}>
        <Animated.View style={status.pulse ? { opacity: pulseAnim } : null}>
          <Ionicons name={status.icon} size={10} color={status.color} />
        </Animated.View>
        <Text style={[styles.statusText, { color: status.color }]}>{status.label}</Text>
      </View>
      
      {/* League Tag */}
      {league && (
        <Text style={styles.leagueTag}>{league.toUpperCase()}</Text>
      )}
      
      {/* Teams with Logos */}
      <View style={styles.teamsContainer}>
        <TeamLogo logoUrl={awayLogo} teamName={awayTeamFull} size={18} />
        <Text style={styles.teamText} numberOfLines={1}>{awayTeam}</Text>
        <Text style={styles.vsText}>@</Text>
        <Text style={styles.teamText} numberOfLines={1}>{homeTeam}</Text>
        <TeamLogo logoUrl={homeLogo} teamName={homeTeam} size={18} />
      </View>
      
      {/* Time */}
      <Text style={styles.timeText}>{formatGameTime(fixture.start_date)}</Text>
      
      {/* ⭐ SGP Cache Indicator - clickable to view SGPs */}
      {showSGPIndicator && hasCachedSGPs && (
        <TouchableOpacity 
          style={styles.sgpIndicator}
          onPress={(e) => {
            e.stopPropagation();
            if (onSGPPress) onSGPPress(fixture);
          }}
          activeOpacity={0.7}
        >
          <View style={styles.sgpDot} />
          <Text style={styles.sgpIndicatorText}>SGP</Text>
        </TouchableOpacity>
      )}
      
      {/* Divider */}
      <View style={styles.divider} />
    </TouchableOpacity>
  );
};

const GameTicker = ({ fixtures = [], isVisible = true, onToggle, isLoading = false, isCollapsed = false, onCollapseToggle }) => {
  const { navigateTo, currentScreen, setRoutePayloadDirect } = useNavigation();
  const { tickerNameFormat, showTickerSGPIndicator, tickerSGPOnly, sgpCacheStatus } = useTicker();
  const { addBackgroundQuery, canAddBackgroundQuery } = useBackgroundQuery();
  const { userProfile } = useAuth();
  
  // Check if background query mode is enabled
  const backgroundQueryMode = userProfile?.preferences?.backgroundQueryMode === true;
  
  const scrollX = useRef(new Animated.Value(0)).current;
  const speedMultiplier = useRef(new Animated.Value(1)).current; // 1 = normal, 0 = stopped
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(screenWidth);
  const animationRef = useRef(null);
  const lastScrollPosition = useRef(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [tickerSpeed, setTickerSpeed] = useState(1); // Display: 1x, 2x, 3x → Actual: 1x, 3x, 5x
  
  // Map display speed to actual multiplier
  const speedMultiplierMap = { 1: 1, 2: 3, 3: 5 };
  
  // ⭐ Scroll speed boost - user can scroll horizontally to speed up/slow down
  const scrollSpeedBoost = useRef(0); // Range: -1 (reverse/slow) to +3 (fast forward)
  const scrollDecayInterval = useRef(null);
  
  // Sort fixtures: LIVE first, then SOON, then UPCOMING, then ENDED
  // Also filter to SGP-only if that mode is enabled
  const sortedFixtures = useMemo(() => {
    let filtered = [...fixtures];
    
    // ⭐ Filter to only show games with cached SGPs if SGP-only mode is enabled
    if (tickerSGPOnly && Object.keys(sgpCacheStatus).length > 0) {
      filtered = filtered.filter(f => {
        const fixtureId = f.fixture_id || f.id;
        return sgpCacheStatus[fixtureId] === true;
      });
    }
    
    return filtered.sort((a, b) => {
      const statusOrder = { live: 0, soon: 1, upcoming: 2, ended: 3 };
      const aStatus = getGameStatus(a.start_date).status;
      const bStatus = getGameStatus(b.start_date).status;
      
      if (statusOrder[aStatus] !== statusOrder[bStatus]) {
        return statusOrder[aStatus] - statusOrder[bStatus];
      }
      
      // Within same status, sort by time
      const getTime = (f) => {
        const date = parseDate(f.start_date);
        return date ? date.getTime() : 0;
      };
      return getTime(a) - getTime(b);
    });
  }, [fixtures, tickerSGPOnly, sgpCacheStatus]);
  
  // Duplicate fixtures for seamless loop
  const displayFixtures = useMemo(() => {
    if (sortedFixtures.length === 0) return [];
    // Triple the fixtures for smooth infinite scroll
    return [...sortedFixtures, ...sortedFixtures, ...sortedFixtures];
  }, [sortedFixtures]);
  
  // Calculate total content width (approximate)
  const itemWidth = 280; // Approximate width per ticker item
  const totalWidth = sortedFixtures.length * itemWidth;
  
  // Calculate unique leagues in order of appearance
  const leaguePositions = useMemo(() => {
    const positions = [];
    const seenLeagues = new Set();
    let currentPosition = 0;
    
    sortedFixtures.forEach((fixture, index) => {
      const league = (fixture.league || '').toUpperCase();
      if (league && !seenLeagues.has(league)) {
        seenLeagues.add(league);
        positions.push({
          league,
          startIndex: index,
          position: currentPosition
        });
      }
      currentPosition += 280; // itemWidth
    });
    
    // Debug log to check leagues
    if (positions.length > 0) {
      console.log('[Ticker] Found', positions.length, 'unique leagues:', positions.map(p => p.league).join(', '));
    }
    
    return positions;
  }, [sortedFixtures]);
  
  // Calculate current league and next league based on scroll position
  const [currentLeagueIndex, setCurrentLeagueIndex] = useState(0);
  
  const nextLeague = useMemo(() => {
    if (leaguePositions.length <= 1) return null;
    const nextIdx = (currentLeagueIndex + 1) % leaguePositions.length;
    return leaguePositions[nextIdx];
  }, [leaguePositions, currentLeagueIndex]);
  
  // Update current league based on scroll position
  useEffect(() => {
    if (leaguePositions.length === 0 || totalWidth === 0) return;
    
    const checkInterval = setInterval(() => {
      const currentPos = Math.abs(lastScrollPosition.current % totalWidth);
      
      // Find which league we're currently viewing
      for (let i = leaguePositions.length - 1; i >= 0; i--) {
        if (currentPos >= leaguePositions[i].position - 140) {
          if (currentLeagueIndex !== i) {
            setCurrentLeagueIndex(i);
          }
          break;
        }
      }
    }, 500); // Check every 500ms
    
    return () => clearInterval(checkInterval);
  }, [leaguePositions, totalWidth, currentLeagueIndex]);
  
  // Skip animation state ref
  const isSkippingRef = useRef(false);
  const skipCompletedRef = useRef(false); // Flag to signal animate loop to sync position
  
  // Function to skip to next league
  const skipToNextLeague = () => {
    if (!nextLeague || leaguePositions.length <= 1) return;
    
    console.log('[Ticker] Skipping to league:', nextLeague.league, 'at position:', nextLeague.position);
    
    // Mark that we're skipping - this will pause the continuous animation
    isSkippingRef.current = true;
    
    // Calculate target position (negative because we scroll left)
    const targetPosition = -nextLeague.position;
    
    // Stop current scroll animation
    scrollX.stopAnimation();
    
    // Animate smoothly to the next league
    Animated.timing(scrollX, {
      toValue: targetPosition,
      duration: 600,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (finished) {
        // Update the position ref after animation completes
        lastScrollPosition.current = targetPosition;
        // Signal that skip completed - animate loop will sync from this
        skipCompletedRef.current = true;
        // Resume continuous animation
        isSkippingRef.current = false;
      }
    });
    
    // Update the current league index
    const nextIdx = leaguePositions.findIndex(l => l.league === nextLeague.league);
    if (nextIdx !== -1) {
      setCurrentLeagueIndex(nextIdx);
    }
  };
  
  // Smooth hover transition - animate speed multiplier
  useEffect(() => {
    Animated.timing(speedMultiplier, {
      toValue: isHovered ? 0 : 1,
      duration: 600, // Smooth 600ms transition
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false, // Can't use native driver for this
    }).start();
  }, [isHovered]);
  
  // ⭐ Handle horizontal scroll to control ticker speed
  const handleWheel = useCallback((e) => {
    // Only respond to horizontal scroll (deltaX)
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && Math.abs(e.deltaX) > 5) {
      e.preventDefault();
      
      // Scroll right (positive deltaX) = speed up
      // Scroll left (negative deltaX) = slow down
      const delta = e.deltaX * 0.02; // Sensitivity
      scrollSpeedBoost.current = Math.max(-0.8, Math.min(4, scrollSpeedBoost.current + delta));
      
      // Clear existing decay and start new one
      if (scrollDecayInterval.current) {
        clearInterval(scrollDecayInterval.current);
      }
      
      // Gradually decay boost back to 0
      scrollDecayInterval.current = setInterval(() => {
        if (Math.abs(scrollSpeedBoost.current) < 0.05) {
          scrollSpeedBoost.current = 0;
          clearInterval(scrollDecayInterval.current);
          scrollDecayInterval.current = null;
        } else {
          scrollSpeedBoost.current *= 0.92; // Decay towards 0
        }
      }, 50);
    }
  }, []);
  
  // Cleanup decay interval on unmount
  useEffect(() => {
    return () => {
      if (scrollDecayInterval.current) {
        clearInterval(scrollDecayInterval.current);
      }
    };
  }, []);

  // Infinite scroll animation with smooth speed control
  useEffect(() => {
    if (!isVisible || sortedFixtures.length === 0 || isPaused) {
      if (animationRef.current) {
        animationRef.current.stop();
      }
      return;
    }
    
    // Base speed: 40ms per pixel (25 pixels per second)
    const baseSpeed = 25; // pixels per second
    let startTime = Date.now();
    let currentPosition = lastScrollPosition.current || 0;
    
    // Use requestAnimationFrame for smooth animation with variable speed
    let frameId;
    const animate = () => {
      // If we're in the middle of a skip animation, don't update position
      if (isSkippingRef.current) {
        frameId = requestAnimationFrame(animate);
        return;
      }
      
      // If skip just completed, sync local position from the ref
      if (skipCompletedRef.current) {
        currentPosition = lastScrollPosition.current;
        skipCompletedRef.current = false;
        console.log('[Ticker] Synced position after skip:', currentPosition);
      }
      
      const now = Date.now();
      const deltaTime = (now - startTime) / 1000; // Convert to seconds
      startTime = now;
      
      // Get current speed multiplier value (hover effect)
      const hoverSpeed = speedMultiplier._value !== undefined ? speedMultiplier._value : 1;
      
      // ⭐ Combine: actual speed (1/3/5x) × hover effect × scroll boost
      const actualSpeed = speedMultiplierMap[tickerSpeed] || 1;
      const totalSpeed = actualSpeed * hoverSpeed * (1 + scrollSpeedBoost.current);
      
      // Move based on speed and time
      currentPosition -= baseSpeed * totalSpeed * deltaTime;
      
      // Loop back when we've scrolled one set of fixtures
      if (currentPosition <= -totalWidth) {
        currentPosition += totalWidth;
      }
      
      // Update scroll position
      scrollX.setValue(currentPosition);
      lastScrollPosition.current = currentPosition;
      
      // Continue animation
      frameId = requestAnimationFrame(animate);
    };
    
    frameId = requestAnimationFrame(animate);
    
    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [isVisible, sortedFixtures.length, isPaused, totalWidth, tickerSpeed]);
  
  const handleGamePress = async (fixture) => {
    const query = `${fixture.home_team} vs ${fixture.away_team}`;
    const payload = { query };
    
    // Check if background query mode is enabled
    if (backgroundQueryMode && canAddBackgroundQuery) {
      // Add to background queue - no page navigation
      await addBackgroundQuery(query, {
        league: fixture.league,
        sport: fixture.sport,
        fromTicker: true,
      });
      return;
    }
    
    // If already on SgpFlow, directly update the payload to trigger a new query
    if (currentScreen === 'SgpFlow') {
      // Store query in session storage for the effect to pick up
      try { 
        window.sessionStorage.setItem('sgp:lastQuery', query); 
      } catch {}
      // Directly update route payload to trigger the useEffect
      if (setRoutePayloadDirect) {
        setRoutePayloadDirect(payload);
      }
    } else {
      navigateTo('SgpFlow', { payload });
    }
  };
  
  // ⭐ Handle SGP badge click - navigate directly to SGP view for this fixture
  const handleSGPPress = (fixture) => {
    const fixtureId = fixture.fixture_id || fixture.id;
    const query = `${fixture.home_team} vs ${fixture.away_team}`;
    
    // Navigate to SgpFlow with specific fixture context and force SGP mode
    const payload = { 
      query,
      fixtureId,
      mode: 'sgp',
      fromCachedSGP: true, // Flag to indicate we're viewing cached SGPs
      league: fixture.league,
    };
    
    // If already on SgpFlow, directly update the payload
    if (currentScreen === 'SgpFlow') {
      try { 
        window.sessionStorage.setItem('sgp:lastQuery', query);
        window.sessionStorage.setItem('sgp:fixtureId', fixtureId);
        window.sessionStorage.setItem('sgp:fromCached', 'true');
      } catch {}
      if (setRoutePayloadDirect) {
        setRoutePayloadDirect(payload);
      }
    } else {
      navigateTo('SgpFlow', { payload });
    }
  };
  
  const handlePauseToggle = () => {
    setIsPaused(!isPaused);
  };
  
  if (!isVisible) return null;
  
  // Show skeleton loading state
  if (isLoading && sortedFixtures.length === 0) {
    return (
      <View style={styles.container}>
        <BlurView intensity={40} tint="dark" style={styles.blurContainer}>
          <View style={styles.gradientBg} />
          <View style={styles.controls}>
            <View style={[styles.controlBtn, { opacity: 0.5 }]}>
              <Ionicons name="pause" size={12} color="rgba(255,255,255,0.4)" />
            </View>
            {onCollapseToggle && (
              <TouchableOpacity 
                onPress={onCollapseToggle}
                style={styles.controlBtn}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons 
                  name="chevron-up" 
                  size={12} 
                  color="rgba(255,255,255,0.6)" 
                />
              </TouchableOpacity>
            )}
          </View>
          <View style={styles.skeletonWrapper}>
            {/* Show 5 skeleton items */}
            {[0, 1, 2, 3, 4].map((i) => (
              <SkeletonTickerItem key={i} index={i} />
            ))}
          </View>
          {/* Edge fades with blur */}
          <View style={[styles.edgeFade, styles.leftFade]} pointerEvents="none">
            {Platform.OS !== 'web' && (
              <BlurView intensity={20} tint="dark" style={StyleSheet.absoluteFill} />
            )}
          </View>
          <View style={[styles.edgeFade, styles.rightFade]} pointerEvents="none">
            {Platform.OS !== 'web' && (
              <BlurView intensity={20} tint="dark" style={StyleSheet.absoluteFill} />
            )}
          </View>
        </BlurView>
      </View>
    );
  }
  
  // Show empty state only if not loading
  if (sortedFixtures.length === 0) {
    return (
      <View style={styles.container}>
        <BlurView intensity={40} tint="dark" style={styles.blurContainer}>
          <View style={styles.controls}>
            {onCollapseToggle && (
              <TouchableOpacity 
                onPress={onCollapseToggle}
                style={styles.controlBtn}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons 
                  name="chevron-up" 
                  size={12} 
                  color="rgba(255,255,255,0.6)" 
                />
              </TouchableOpacity>
            )}
          </View>
          <View style={styles.emptyState}>
            <Ionicons name="calendar-outline" size={16} color="rgba(255,255,255,0.4)" />
            <Text style={styles.emptyText}>No games available</Text>
          </View>
        </BlurView>
      </View>
    );
  }
  
  return (
    <View style={styles.container}>
      <BlurView intensity={40} tint="dark" style={styles.blurContainer}>
        {/* Background gradient */}
        <View style={styles.gradientBg} />
        
        {/* Control buttons */}
        <View style={styles.controls}>
          <TouchableOpacity 
            onPress={handlePauseToggle}
            style={styles.controlBtn}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Ionicons 
              name={isPaused ? "play" : "pause"} 
              size={12} 
              color="rgba(255,255,255,0.6)" 
            />
          </TouchableOpacity>
          
          {/* Speed buttons */}
          <View style={styles.speedControls}>
            {[1, 2, 3].map((speed) => (
              <TouchableOpacity
                key={speed}
                onPress={() => setTickerSpeed(speed)}
                style={[
                  styles.speedBtn,
                  tickerSpeed === speed && styles.speedBtnActive
                ]}
                hitSlop={{ top: 5, bottom: 5, left: 2, right: 2 }}
              >
                <Text style={[
                  styles.speedBtnText,
                  tickerSpeed === speed && styles.speedBtnTextActive
                ]}>
                  {speed}x
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          
          {onCollapseToggle && (
            <TouchableOpacity 
              onPress={onCollapseToggle}
              style={styles.controlBtn}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons 
                name={isCollapsed ? "chevron-down" : "chevron-up"} 
                size={12} 
                color="rgba(255,255,255,0.6)" 
              />
            </TouchableOpacity>
          )}
        </View>
        
        {/* Scrolling content */}
        <View 
          style={styles.tickerWrapper}
          onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
          {...(Platform.OS === 'web' ? {
            onMouseEnter: () => setIsHovered(true),
            onMouseLeave: () => setIsHovered(false),
            onWheel: handleWheel,
          } : {})}
        >
          <Animated.View 
            style={[
              styles.tickerContent,
              { 
                transform: [{ translateX: scrollX }],
                width: displayFixtures.length * itemWidth,
              }
            ]}
          >
            {displayFixtures.map((fixture, index) => {
              const fixtureId = fixture.fixture_id || fixture.id;
              return (
                <TickerItem 
                  key={`${fixtureId}-${index}`}
                  fixture={fixture}
                  index={index}
                  nameFormat={tickerNameFormat}
                  onPress={() => handleGamePress(fixture)}
                  onSGPPress={handleSGPPress}
                  showSGPIndicator={showTickerSGPIndicator}
                  hasCachedSGPs={sgpCacheStatus[fixtureId] === true}
                />
              );
            })}
          </Animated.View>
        </View>
        
        {/* Edge fades with blur */}
        <View style={[styles.edgeFade, styles.leftFade]} pointerEvents="none">
          {Platform.OS !== 'web' && (
            <BlurView intensity={20} tint="dark" style={StyleSheet.absoluteFill} />
          )}
        </View>
        <View style={[styles.edgeFade, styles.rightFade]} pointerEvents="none">
          {Platform.OS !== 'web' && (
            <BlurView intensity={20} tint="dark" style={StyleSheet.absoluteFill} />
          )}
        </View>
        
        {/* Right side controls - Next League button */}
        <View style={styles.controlsRight}>
          {leaguePositions.length > 1 && nextLeague ? (
            <TouchableOpacity 
              onPress={skipToNextLeague}
              style={styles.nextLeagueBtn}
              activeOpacity={0.7}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Text style={styles.nextLeagueLabel}>{nextLeague.league}</Text>
              <Ionicons name="chevron-forward" size={14} color="#c4b5fd" />
            </TouchableOpacity>
          ) : (
            <View style={styles.leagueIndicator}>
              <Text style={styles.currentLeagueText}>
                {leaguePositions[0]?.league || ''}
              </Text>
            </View>
          )}
        </View>
      </BlurView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    zIndex: 9998,
    overflow: 'hidden',
  },
  blurContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.06)',
    overflow: 'hidden',
    position: 'relative',
  },
  gradientBg: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
  controls: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 12,
    paddingRight: 8,
    gap: 6,
    zIndex: 10,
  },
  controlBtn: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.08)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  speedControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    marginLeft: 4,
  },
  speedBtn: {
    paddingHorizontal: 5,
    paddingVertical: 3,
    borderRadius: 4,
    backgroundColor: 'transparent',
  },
  speedBtnActive: {
    backgroundColor: 'rgba(255,255,255,0.15)',
  },
  speedBtnText: {
    fontSize: 9,
    fontWeight: '600',
    color: 'rgba(255,255,255,0.35)',
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
  },
  speedBtnTextActive: {
    color: 'rgba(255,255,255,0.8)',
  },
  tickerWrapper: {
    flex: 1,
    overflow: 'hidden',
  },
  tickerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  tickerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    gap: 8,
    height: 28,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    gap: 4,
  },
  statusText: {
    fontSize: 9,
    fontWeight: '700',
    letterSpacing: 0.5,
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
  },
  leagueTag: {
    fontSize: 10,
    fontWeight: '600',
    color: 'rgba(255,255,255,0.4)',
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  teamsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  teamText: {
    fontSize: 12,
    fontWeight: '600',
    color: 'rgba(255,255,255,0.9)',
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
    maxWidth: 80,
  },
  vsText: {
    fontSize: 10,
    fontWeight: '500',
    color: 'rgba(255,255,255,0.4)',
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
  },
  timeText: {
    fontSize: 10,
    fontWeight: '500',
    color: 'rgba(255,255,255,0.5)',
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
  },
  divider: {
    width: 1,
    height: 16,
    backgroundColor: 'rgba(255,255,255,0.1)',
    marginLeft: 8,
  },
  edgeFade: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 80,
    zIndex: 5,
  },
  leftFade: {
    left: 50, // After the control buttons
    ...(Platform.OS === 'web' ? {
      background: 'linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0.9) 20%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0) 100%)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      maskImage: 'linear-gradient(to right, black 0%, black 60%, transparent 100%)',
      WebkitMaskImage: 'linear-gradient(to right, black 0%, black 60%, transparent 100%)',
    } : {
      backgroundColor: 'transparent',
    }),
  },
  rightFade: {
    right: 70, // Offset for right controls
    ...(Platform.OS === 'web' ? {
      background: 'linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,0.9) 20%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0) 100%)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      maskImage: 'linear-gradient(to left, black 0%, black 60%, transparent 100%)',
      WebkitMaskImage: 'linear-gradient(to left, black 0%, black 60%, transparent 100%)',
    } : {
      backgroundColor: 'transparent',
    }),
  },
  emptyState: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 4,
  },
  emptyText: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.4)',
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
  },
  skeletonWrapper: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    overflow: 'hidden',
  },
  controlsRight: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: 10,
    paddingLeft: 4,
    gap: 6,
    zIndex: 10,
  },
  nextLeagueBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 8,
    backgroundColor: 'rgba(139, 92, 246, 0.2)',
    borderWidth: 1,
    borderColor: 'rgba(139, 92, 246, 0.3)',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  nextLeagueLabel: {
    fontSize: 10,
    fontWeight: '600',
    color: '#c4b5fd',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
  },
  leagueIndicator: {
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  currentLeagueText: {
    fontSize: 10,
    fontWeight: '500',
    color: 'rgba(255,255,255,0.4)',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
  },
  // ⭐ SGP Cache Indicator Styles
  sgpIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: 'rgba(16, 185, 129, 0.15)',
    borderWidth: 1,
    borderColor: 'rgba(16, 185, 129, 0.25)',
  },
  sgpDot: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
    backgroundColor: '#10B981',
  },
  sgpIndicatorText: {
    fontSize: 8,
    fontWeight: '700',
    color: '#10B981',
    letterSpacing: 0.3,
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
  },
});

export default GameTicker;

